import {
    BackendV2Client,
    DEFAULT_POLL_INTERVAL_MS,
    DEFAULT_TIMEOUT_MS,
} from '~/backend/backend-v2-client';
import {
    CreateRegistrationTaskPayload,
    RegistrationClient,
} from '~/backend/registration-client';
import {
    CompositeCbctClient,
    CreateCompositeCbctTaskPayload,
} from '~/backend/composite-cbct-client';
import { BlobUploadType, RequestParameters } from '~/backend/backend-client';
import { handleError } from '~/utils/errors';

export type DatasetUploadSummary = {
    datasetId: string;
    totalFiles: number;
    uploadedFiles: number;
};

/**
 * Creates a new dataset entry on the backend and returns its ID.
 */
export async function createImageDataset(
    client: BackendV2Client,
    request: RequestParameters,
    taskUID: string,
    nExpectedFiles: number
): Promise<string | null> {
    try {
        const datasetResponse = await client.createImageDataset({
            ...request,
            nExpectedFiles: nExpectedFiles.toString(),
            taskUID,
        });

        if (!datasetResponse || !(datasetResponse as { id: string }).id) {
            handleError('Failed to create dataset on backend.');
            return null;
        }

        const datasetId = (datasetResponse as { id: string }).id;
        return datasetId;
    } catch (error) {
        handleError(error, 'Error creating dataset on backend.');
        return null;
    }
}

/**
 * Uploads all files to an existing dataset on the backend.
 */
export async function uploadDatasetFiles(
    client: BackendV2Client,
    request: RequestParameters,
    datasetId: string,
    files: BlobUploadType[]
): Promise<number> {
    let uploadedCount = 0;

    try {
        await Promise.all(
            files.map(async (file, idx) => {
                const fileSuccessfullyUploaded =
                    await client.uploadFileToDataset(
                        request,
                        { file: file.file, filename: file.filename },
                        datasetId,
                        idx
                    );

                if (fileSuccessfullyUploaded) {
                    uploadedCount++;
                }
            })
        );

        return uploadedCount;
    } catch (error) {
        handleError(error, 'Error uploading dataset files.');
        return uploadedCount;
    }
}

/**
 * Creates a registration (adapt+) task on the backend.
 */
export async function createRegistrationTask(
    client: RegistrationClient,
    request: RequestParameters,
    payload: CreateRegistrationTaskPayload
): Promise<string | null> {
    try {
        const response = (await client.createRegistrationTask(
            request,
            payload
        )) as { id?: string };

        if (!response || !response.id) {
            handleError(
                'Failed to create registration task: missing id in response.'
            );
            return null;
        }

        return response.id;
    } catch (error) {
        handleError(error, 'Error creating registration task.');
        return null;
    }
}

/**
 * Creates a composite-CBCT task on the backend.
 */
export async function createCompositeCbctTask(
    client: CompositeCbctClient,
    request: RequestParameters,
    payload: CreateCompositeCbctTaskPayload
): Promise<string | null> {
    try {
        const response = (await client.createCompositeCbctTask(
            request,
            payload
        )) as { id?: string };

        if (!response || !response.id) {
            handleError(
                'Failed to create composite CBCT task: missing id in response.'
            );
            return null;
        }

        return response.id;
    } catch (error) {
        handleError(error, 'Error creating composite CBCT task.');
        return null;
    }
}

/**
 * Monitors a task until results are available or timeout occurs.
 * Generic across task families — the client's `taskPathSegment`
 * decides which endpoint is polled.
 */
export async function monitorCreatedTask(
    client: BackendV2Client,
    request: RequestParameters,
    taskId: string,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    timeoutMs = DEFAULT_TIMEOUT_MS
): Promise<{ resultIds: string[] } | null> {
    try {
        const results = await client.monitorTask(
            request,
            taskId,
            pollIntervalMs,
            timeoutMs
        );

        if (!results) {
            handleError('Task failed or timed out.');
            return null;
        }

        return results;
    } catch (error) {
        handleError(error, 'Error monitoring task.');
        return null;
    }
}

/**
 * Downloads result files for a completed task.
 */
export async function downloadTaskResults(
    client: BackendV2Client,
    request: RequestParameters,
    taskId: string,
    resultIds: string[]
): Promise<File[]> {
    const downloadedFiles: File[] = [];

    for (const resultId of resultIds) {
        try {
            const blob = await client.fetchTaskResult(
                request,
                taskId,
                resultId
            );
            if (blob) {
                downloadedFiles.push(new File([blob], `${resultId}.dcm`));
            }
        } catch (error) {
            handleError(error, `Error downloading result file ${resultId}`);
        }
    }

    return downloadedFiles;
}

/**
 * Deletes results for a given task.
 */
export async function deleteTaskResult(
    client: BackendV2Client,
    request: RequestParameters,
    taskId: string
): Promise<boolean> {
    try {
        const taskResultDeleted = await client.deleteTask(request, taskId);
        return taskResultDeleted;
    } catch (error) {
        handleError(error, `Error deleting results for task ${taskId}`);
        return false;
    }
}
